import * as THREE from 'three';

export interface GameObject {
    mesh: THREE.Object3D;
    update(deltaTime: number): void;
    dispose(): void;
}

export class FloatingDroid implements GameObject {
    public mesh: THREE.Group;
    public isDestroyed: boolean = false;
    
    private velocity: THREE.Vector3 = new THREE.Vector3();
    private rotationSpeed: THREE.Vector3 = new THREE.Vector3();

    constructor() {
        this.mesh = new THREE.Group();
        this.createDroidModel();
        
        // Random initial rotation
        this.rotationSpeed.set(
            (Math.random() - 0.5) * 1,
            (Math.random() - 0.5) * 1,
            (Math.random() - 0.5) * 1
        );
    }

    private createDroidModel() {
        // Simple Droid: Sphere body with some details
        const geometry = new THREE.SphereGeometry(0.5, 32, 16);
        const material = new THREE.MeshStandardMaterial({ 
            color: 0xdddddd,
            roughness: 0.4,
            metalness: 0.8
        });
        const body = new THREE.Mesh(geometry, material);
        this.mesh.add(body);

        // Eye
        const eyeGeo = new THREE.SphereGeometry(0.15, 16, 16);
        const eyeMat = new THREE.MeshStandardMaterial({ 
            color: 0x111111,
            roughness: 0.1,
            metalness: 1.0,
            emissive: 0xff0000,
            emissiveIntensity: 0.5
        });
        const eye = new THREE.Mesh(eyeGeo, eyeMat);
        eye.position.set(0, 0.2, 0.4);
        this.mesh.add(eye);

        // Antenna
        const antGeo = new THREE.CylinderGeometry(0.02, 0.02, 0.6);
        const ant = new THREE.Mesh(antGeo, material);
        ant.position.set(0, 0.6, 0);
        this.mesh.add(ant);
        
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;
        
        // User Data for raycasting
        this.mesh.userData = { isTarget: true, entity: this };
        body.userData = { isTarget: true, entity: this };
    }

    public applyForce(force: THREE.Vector3) {
        this.velocity.add(force);
    }

    public update(deltaTime: number) {
        if (this.isDestroyed) return;

        // Apply drag
        this.velocity.multiplyScalar(0.98);

        // Update position
        this.mesh.position.addScaledVector(this.velocity, deltaTime);

        // Rotate
        this.mesh.rotation.x += this.rotationSpeed.x * deltaTime;
        this.mesh.rotation.y += this.rotationSpeed.y * deltaTime;
        this.mesh.rotation.z += this.rotationSpeed.z * deltaTime;
    }
    
    public destroy() {
        this.isDestroyed = true;
        this.mesh.visible = false;
    }

    public dispose() {
        this.mesh.traverse((node) => {
            if (node instanceof THREE.Mesh) {
                node.geometry.dispose();
                (node.material as THREE.Material).dispose();
            }
        });
    }
}

export class LightningEffect implements GameObject {
    public mesh: THREE.Line;
    private lifeTime: number = 0;
    private maxLifeTime: number = 0.2; // Short burst

    constructor(start: THREE.Vector3, end: THREE.Vector3) {
        // Create jagged line
        const segments = 10;
        const geometry = new THREE.BufferGeometry();
        const positions = [];
        
        let current = start.clone();
        const dir = end.clone().sub(start).normalize();
        const dist = start.distanceTo(end);
        const step = dist / segments;

        for (let i = 0; i <= segments; i++) {
             positions.push(current.x, current.y, current.z);
             
             if (i < segments) {
                 // Advance and jitter
                 current.addScaledVector(dir, step);
                 current.add(new THREE.Vector3(
                     (Math.random() - 0.5) * 0.5,
                     (Math.random() - 0.5) * 0.5,
                     (Math.random() - 0.5) * 0.5
                 ));
             }
        }

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        
        const material = new THREE.LineBasicMaterial({
            color: 0x00d4ff, // Blue lightning
            linewidth: 2, 
            opacity: 0.8,
            transparent: true,
            blending: THREE.AdditiveBlending
        });

        this.mesh = new THREE.Line(geometry, material);
    }

    public update(deltaTime: number) {
        this.lifeTime += deltaTime;
        if (this.lifeTime > this.maxLifeTime) {
            this.mesh.visible = false;
        } else {
            (this.mesh.material as THREE.LineBasicMaterial).opacity = 1 - (this.lifeTime / this.maxLifeTime);
        }
    }

    public isDead(): boolean {
        return this.lifeTime > this.maxLifeTime;
    }

    public dispose() {
        this.mesh.geometry.dispose();
        (this.mesh.material as THREE.Material).dispose();
    }
}

export class ExplosionEffect implements GameObject {
    public mesh: THREE.Points;
    private velocities: THREE.Vector3[] = [];
    private lifeTime: number = 0;
    private maxLifeTime: number = 1.0;

    constructor(position: THREE.Vector3) {
        const particleCount = 20;
        const geometry = new THREE.BufferGeometry();
        const positions = [];
        
        for (let i = 0; i < particleCount; i++) {
            positions.push(position.x, position.y, position.z);
            
            this.velocities.push(new THREE.Vector3(
                (Math.random() - 0.5) * 5,
                (Math.random() - 0.5) * 5,
                (Math.random() - 0.5) * 5
            ));
        }
        
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        
        const material = new THREE.PointsMaterial({
            color: 0xffaa00,
            size: 0.2,
            transparent: true,
            blending: THREE.AdditiveBlending
        });
        
        this.mesh = new THREE.Points(geometry, material);
    }
    
    public update(deltaTime: number) {
        this.lifeTime += deltaTime;
        
        const positions = this.mesh.geometry.attributes.position.array as Float32Array;
        
        for (let i = 0; i < this.velocities.length; i++) {
            positions[i * 3] += this.velocities[i].x * deltaTime;
            positions[i * 3 + 1] += this.velocities[i].y * deltaTime;
            positions[i * 3 + 2] += this.velocities[i].z * deltaTime;
        }
        
        this.mesh.geometry.attributes.position.needsUpdate = true;
        (this.mesh.material as THREE.PointsMaterial).opacity = 1 - (this.lifeTime / this.maxLifeTime);
    }
    
    public isDead(): boolean {
        return this.lifeTime > this.maxLifeTime;
    }
    
    public dispose() {
        this.mesh.geometry.dispose();
        (this.mesh.material as THREE.Material).dispose();
    }
}
